// Admin patron program — config + audit log.
//
// Lets an operator inspect program state (active count vs cap, status,
// window close date) and edit the safe-to-edit config keys with an
// audit-logged reason. Below the config form is the recent audit log.
import { useCallback, useEffect, useState } from "react";
import { Button } from "../../components/ui/Button";
import { Card, CardSubtle, CardTitle } from "../../components/ui/Card";
import { Input, Label, Textarea } from "../../components/ui/Input";
import { Spinner } from "../../components/ui/Spinner";
import { api } from "../../lib/api";

interface ProgramStatus {
  founding: {
    is_open: boolean;
    status: string;
    cohort_cap: number;
    current_count: number;
    window_close_date: string | null;
    minimum_usd: number;
  };
  pledge: { minimum_usd: number };
}

interface AuditEntry {
  id: string;
  target_type: string;
  target_id: string | null;
  action: string;
  actor_id: string;
  reason: string;
  details: Record<string, unknown> | null;
  created_at: string;
  profiles: { username: string } | null;
}

interface AuditResponse {
  entries: AuditEntry[];
  total: number;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export default function AdminPatronProgramPage() {
  const [status, setStatus] = useState<ProgramStatus | null>(null);
  const [audit, setAudit] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [closeDate, setCloseDate] = useState("");
  const [programStatus, setProgramStatus] = useState("active");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [statusRes, auditRes] = await Promise.all([
        api<ProgramStatus>("/patrons/program-status"),
        api<AuditResponse>("/patrons/admin/audit?limit=100"),
      ]);
      setStatus(statusRes);
      setAudit(auditRes.entries);
      if (statusRes.founding.window_close_date) {
        setCloseDate(statusRes.founding.window_close_date.slice(0, 10));
      }
      setProgramStatus(statusRes.founding.status);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  async function updateConfig(key: string, value: unknown) {
    if (reason.trim().length < 3) {
      setSubmitError("Reason is required (min 3 chars).");
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      await api(`/patrons/admin/program/config`, {
        method: "POST",
        body: { key, value, reason: reason.trim() },
      });
      setReason("");
      await fetchAll();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Update failed");
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <Card>
        <div style={styles.loadingWrap}>
          <Spinner />
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <div style={styles.errorBar}>{error}</div>
      </Card>
    );
  }

  const f = status?.founding;

  return (
    <div>
      <div style={styles.header}>
        <h1 style={styles.h1}>Patron program</h1>
        <CardSubtle>
          Founding Patron program state and operator overrides. Every change
          requires a reason and is logged.
        </CardSubtle>
      </div>

      <div style={styles.statsRow}>
        <Card style={{ flex: 1 }}>
          <CardSubtle>Status</CardSubtle>
          <div style={styles.statValue}>{f?.status ?? "—"}</div>
          <CardSubtle>
            {f?.is_open ? "Open for new sign-ups" : "Closed"}
          </CardSubtle>
        </Card>
        <Card style={{ flex: 1 }}>
          <CardSubtle>Founders</CardSubtle>
          <div style={styles.statValue}>
            {f?.current_count ?? 0} / {f?.cohort_cap ?? 100}
          </div>
        </Card>
        <Card style={{ flex: 1 }}>
          <CardSubtle>Window closes</CardSubtle>
          <div style={styles.statValue}>
            {f?.window_close_date
              ? new Date(f.window_close_date).toLocaleDateString()
              : "—"}
          </div>
        </Card>
      </div>

      <Card style={{ marginBottom: 16 }}>
        <CardTitle>Edit config</CardTitle>
        <CardSubtle style={{ marginBottom: 12 }}>
          Set a reason once below; it applies to whichever Save you click.
        </CardSubtle>

        <Label>Reason</Label>
        <Textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={2}
          placeholder="Why are you making this change?"
        />

        <div style={{ marginTop: 16, display: "grid", gap: 16 }}>
          <div>
            <Label>Window close date (YYYY-MM-DD)</Label>
            <div style={styles.editRow}>
              <Input
                type="date"
                value={closeDate}
                onChange={(e) => setCloseDate(e.target.value)}
              />
              <Button
                size="sm"
                onClick={() =>
                  updateConfig(
                    "founding_patron_window_close_date",
                    closeDate ? new Date(closeDate).toISOString() : null,
                  )
                }
                disabled={submitting}
              >
                Save
              </Button>
            </div>
          </div>

          <div>
            <Label>Program status</Label>
            <div style={styles.editRow}>
              <select
                value={programStatus}
                onChange={(e) => setProgramStatus(e.target.value)}
                style={styles.select}
              >
                <option value="active">active</option>
                <option value="closed_full">closed_full</option>
                <option value="closed_expired">closed_expired</option>
              </select>
              <Button
                size="sm"
                onClick={() =>
                  updateConfig("founding_patron_program_status", programStatus)
                }
                disabled={submitting}
              >
                Save
              </Button>
            </div>
          </div>
        </div>

        {submitError && (
          <div style={{ ...styles.errorBar, marginTop: 12 }}>{submitError}</div>
        )}
      </Card>

      <Card padding={0}>
        <div style={{ padding: 16 }}>
          <CardTitle>Audit log</CardTitle>
          <CardSubtle>Latest 100 patron admin actions.</CardSubtle>
        </div>
        <table style={styles.table}>
          <thead>
            <tr style={styles.tableHead}>
              <Th>When</Th>
              <Th>Actor</Th>
              <Th>Target</Th>
              <Th>Action</Th>
              <Th>Reason</Th>
            </tr>
          </thead>
          <tbody>
            {audit.length === 0 ? (
              <tr>
                <td colSpan={5} style={{ padding: 24, textAlign: "center" }}>
                  <CardSubtle>No audit entries yet.</CardSubtle>
                </td>
              </tr>
            ) : (
              audit.map((e) => (
                <tr key={e.id} style={styles.tableRow}>
                  <Td>
                    <CardSubtle style={{ fontSize: 12 }}>
                      {formatDate(e.created_at)}
                    </CardSubtle>
                  </Td>
                  <Td>@{e.profiles?.username ?? "?"}</Td>
                  <Td>
                    {e.target_type}
                    <CardSubtle style={{ fontSize: 12 }}>
                      {e.target_id ? e.target_id.slice(0, 8) : ""}
                    </CardSubtle>
                  </Td>
                  <Td>{e.action}</Td>
                  <Td>
                    <CardSubtle style={{ fontSize: 13, fontStyle: "italic" }}>
                      {e.reason}
                    </CardSubtle>
                  </Td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return <th style={styles.th}>{children}</th>;
}
function Td({ children }: { children: React.ReactNode }) {
  return <td style={styles.td}>{children}</td>;
}

const styles: Record<string, React.CSSProperties> = {
  header: { marginBottom: 16 },
  h1: { margin: 0, fontSize: 24, fontWeight: 700, color: "var(--text-primary)" },
  statsRow: { display: "flex", gap: 12, marginBottom: 16 },
  statValue: {
    fontSize: 28,
    fontWeight: 700,
    color: "var(--text-primary)",
    margin: "4px 0",
  },
  editRow: { display: "flex", gap: 8, alignItems: "center" },
  select: {
    flex: 1,
    padding: "6px 10px",
    borderRadius: "var(--radius-sm)",
    background: "var(--surface)",
    color: "var(--text-primary)",
    border: "1px solid var(--border)",
  },
  loadingWrap: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "48px 0",
  },
  errorBar: {
    background: "rgba(239,68,68,0.1)",
    color: "var(--danger, #ef4444)",
    padding: 12,
    borderRadius: "var(--radius-sm)",
  },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 14 },
  tableHead: { background: "rgba(255,255,255,0.02)", textAlign: "left" },
  tableRow: { borderTop: "1px solid var(--border)" },
  th: {
    padding: "10px 12px",
    fontSize: 12,
    fontWeight: 600,
    color: "var(--text-secondary)",
    textTransform: "uppercase",
    letterSpacing: 0.3,
  },
  td: { padding: 12, verticalAlign: "top", color: "var(--text-primary)" },
};
